<template>
  <div class="rd-overlay" @click.self="$emit('close')">
    <aside class="rd-drawer" role="dialog" :aria-label="`${type} referral ${referral.displayname || ''}`">
      <!-- ── Header ── -->
      <header class="rd-header">
        <div class="rd-header__main">
          <span class="rd-type">{{ type }}</span>
          <h2 class="rd-title">{{ referral.displayname || '—' }}</h2>
          <p class="rd-sub">
            <span v-if="referral.loname"><i class="fa-regular fa-user"></i> {{ referral.loname }}</span>
            <span v-if="referral.location"><i class="fa-regular fa-location-dot"></i> {{ referral.location }}</span>
            <span v-if="referral.loanamount"><i class="fa-regular fa-dollar-sign"></i> {{ referral.loanamount }}</span>
          </p>
        </div>
        <div class="rd-header__actions">
          <button type="button" class="rd-btn" :disabled="detailLoading" @click="openDetail">
            <i class="fa-regular fa-file-lines"></i> Full application
          </button>
          <button type="button" class="rd-close" aria-label="Close" @click="$emit('close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      <!-- ── Status tracker ── -->
      <section class="rd-section">
        <div class="rd-section__head">
          <h3>Status</h3>
          <span v-if="referral.statuschangedat" class="rd-muted">updated {{ when(statusChangedAt) }}</span>
        </div>

        <div v-if="currentStage?.isofframp" class="rd-offramp" :style="{ borderColor: currentStage.color, color: currentStage.color }">
          <i class="fa-solid fa-circle-xmark"></i> {{ currentStage.label }}
        </div>

        <ol class="rd-stepper" :class="{ 'is-dimmed': currentStage?.isofframp }">
          <li v-for="(stage, i) in mainStages"
              :key="stage.code"
              class="rd-step"
              :class="{ 'is-done': i < currentIndex, 'is-current': i === currentIndex, 'is-clickable': canSetStatus && stage.code !== referral.statuscode }"
              :style="i <= currentIndex ? { '--step-color': stage.color || '#398b87' } : null"
              :title="canSetStatus && stage.code !== referral.statuscode ? `Move to ${stage.label}` : stage.label"
              @click="pickStatus(stage)">
            <span class="rd-step__dot">
              <i v-if="i < currentIndex" class="fa-solid fa-check"></i>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="rd-step__label">{{ stage.label }}</span>
          </li>
        </ol>

        <div v-if="canSetStatus && offRamps.length" class="rd-offramp-actions">
          <button v-for="stage in offRamps"
                  :key="stage.code"
                  type="button"
                  class="rd-link"
                  :disabled="stage.code === referral.statuscode"
                  @click="pickStatus(stage)">Mark {{ stage.label.toLowerCase() }}</button>
        </div>

        <div v-if="pendingStage" class="rd-confirm">
          <div class="rd-confirm__title">
            Move from <strong>{{ currentStage?.label || 'New Referral' }}</strong> to <strong>{{ pendingStage.label }}</strong>?
          </div>
          <textarea v-model="statusNote" class="rd-textarea" rows="2" maxlength="4000" placeholder="Optional comment for the history"></textarea>
          <div class="rd-confirm__actions">
            <button type="button" class="rd-btn" @click="pendingStage = null">Cancel</button>
            <button type="button" class="rd-btn rd-btn--primary" :disabled="statusBusy" @click="saveStatus">
              {{ statusBusy ? 'Saving...' : `Move to ${pendingStage.label}` }}
            </button>
          </div>
        </div>
      </section>

      <!-- ── Notes + history ── -->
      <section class="rd-section rd-section--thread">
        <div class="rd-section__head">
          <h3>Notes &amp; activity</h3>
          <button type="button" class="rd-link" :disabled="loading" @click="loadActivity">Refresh</button>
        </div>

        <div v-if="error" class="rd-error">{{ error }}</div>

        <div ref="thread" class="rd-thread">
          <div v-if="loading && !activity.length" class="rd-muted">Loading...</div>
          <template v-for="item in activity" :key="item.kind + item.id">
            <div v-if="item.kind === 'note'" class="rd-note" :class="{ 'is-mine': isMine(item) }">
              <div class="rd-note__meta"><strong>{{ item.authorname || 'Someone' }}</strong> · {{ when(item.createdat) }}</div>
              <div class="rd-note__body">{{ item.body }}</div>
            </div>
            <div v-else class="rd-event">
              <i :class="item.kind === 'created' ? 'fa-regular fa-paper-plane' : 'fa-solid fa-arrow-right-arrow-left'"></i>
              <div>
                <template v-if="item.kind === 'created'">
                  Referral submitted<span v-if="item.authorname"> by <strong>{{ item.authorname }}</strong></span>
                </template>
                <template v-else>
                  <strong>{{ item.authorname || 'Someone' }}</strong> moved it from {{ stageLabel(item.fromstatus) }} to
                  <strong>{{ stageLabel(item.tostatus) }}</strong>
                </template>
                <span class="rd-muted"> · {{ when(item.createdat) }}</span>
                <div v-if="item.body" class="rd-event__body">{{ item.body }}</div>
              </div>
            </div>
          </template>
        </div>

        <div class="rd-composer">
          <textarea v-model="noteText"
                    class="rd-textarea"
                    rows="3"
                    maxlength="4000"
                    placeholder="Write a note for the loan officer and the team... (Ctrl+Enter to post)"
                    @keydown.ctrl.enter.prevent="postNote"
                    @keydown.meta.enter.prevent="postNote"></textarea>
          <button type="button" class="rd-btn rd-btn--primary" :disabled="posting || !noteText.trim()" @click="postNote">
            {{ posting ? 'Posting...' : 'Post note' }}
          </button>
        </div>
      </section>
    </aside>

    <ReverseMortgageDetailModal v-if="type === 'Reverse'" :open="detailOpen" :item="detailItem || {}" @close="detailOpen = false" />
    <CommercialLoanDetailModal v-else :open="detailOpen" :item="detailItem || {}" @close="detailOpen = false" />
  </div>
</template>

<script>
  import ReverseMortgageDetailModal from '../ReverseMortgageDetailModal.vue';
  import CommercialLoanDetailModal from '../CommercialLoanDetailModal.vue';
  import apiService from '../../api/api.service.js';
  import auth from '../../authConfig.js';
  import formatHelpers from '../../helpers/formatHelpers.js';
  import { lowerKeys, utcDate, formatWhen } from './referralHelpers.js';

  /**
   * Side drawer for one Reverse / Commercial referral: status stepper (admins move it, everyone else reads it),
   * the notes thread shared by the LO and the admins, and the full application in the existing detail modal.
   * Opening it marks the referral read (server side, in GetActivity). Emits 'changed' whenever the list's
   * status / note count / unread flag may be stale.
   */
  export default {
    name: 'ReferralDrawer',
    components: { ReverseMortgageDetailModal, CommercialLoanDetailModal },
    props: {
      type: { type: String, required: true },
      referral: { type: Object, required: true },
      stages: { type: Array, default: () => [] },
      canSetStatus: { type: Boolean, default: false },
    },
    emits: ['close', 'changed'],
    data() {
      return {
        activity: [],
        loading: false,
        error: '',
        noteText: '',
        posting: false,
        pendingStage: null,
        statusNote: '',
        statusBusy: false,
        detailOpen: false,
        detailItem: null,
        detailLoading: false,
        myAzureId: (localStorage.getItem('azure_id') || '').toLowerCase(),
      };
    },
    computed: {
      mainStages() { return this.stages.filter(s => !s.isofframp); },
      offRamps() { return this.stages.filter(s => s.isofframp); },
      currentStage() { return this.stages.find(s => s.code === this.referral.statuscode) || null; },
      currentIndex() {
        const i = this.mainStages.findIndex(s => s.code === this.referral.statuscode);
        return i < 0 && !this.currentStage?.isofframp ? 0 : i;
      },
      statusChangedAt() { return utcDate(this.referral.statuschangedat); },
    },
    watch: {
      'referral.id'() {
        this.pendingStage = null;
        this.detailItem = null;
        this.loadActivity();
      },
    },
    created() {
      this.loadActivity();
      document.addEventListener('keydown', this.onKeydown);
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
      async loadActivity() {
        this.loading = true;
        this.error = '';
        try {
          const token = await auth.acquireTokenResponse();
          const res = await apiService.GetReferralActivity(this.type, this.referral.id, token.accessToken);
          this.activity = (res?.data?.data ?? []).map(a => this.toItem(a));
          // Opening it marked it read on the server; let the list and the tab badge catch up.
          if (this.referral.isunread) this.$emit('changed');
          this.scrollThreadToEnd();
        } catch (err) {
          this.error = err?.response?.data?.message || 'Could not load the notes.';
        } finally {
          this.loading = false;
        }
      },
      async postNote() {
        const body = this.noteText.trim();
        if (!body || this.posting) return;
        this.posting = true;
        this.error = '';
        try {
          const token = await auth.acquireTokenResponse();
          const res = await apiService.AddReferralNote(this.type, this.referral.id, body, token.accessToken);
          const item = res?.data?.data;
          if (item) this.activity.push(this.toItem(item));
          this.noteText = '';
          this.scrollThreadToEnd();
          this.$emit('changed');
        } catch (err) {
          this.error = err?.response?.data?.message || 'Could not post the note.';
        } finally {
          this.posting = false;
        }
      },
      pickStatus(stage) {
        if (!this.canSetStatus || stage.code === this.referral.statuscode) return;
        this.pendingStage = stage;
        this.statusNote = '';
      },
      async saveStatus() {
        if (!this.pendingStage) return;
        this.statusBusy = true;
        this.error = '';
        try {
          const token = await auth.acquireTokenResponse();
          await apiService.SetReferralStatus(this.type, this.referral.id, this.pendingStage.code, this.statusNote.trim(), token.accessToken);
          this.pendingStage = null;
          this.$emit('changed');
          await this.loadActivity();
        } catch (err) {
          this.error = err?.response?.data?.message || 'Could not change the status.';
        } finally {
          this.statusBusy = false;
        }
      },
      async openDetail() {
        if (this.detailItem) { this.detailOpen = true; return; }
        this.detailLoading = true;
        try {
          const token = await auth.acquireTokenResponse();
          const res = this.type === 'Reverse'
            ? await apiService.GetReverseMortgageRequestById(this.referral.id, token.accessToken)
            : await apiService.GetCommercialLoanRequestById(this.referral.id, token.accessToken);
          const item = lowerKeys(res?.data?.data ?? {});
          // Same display formatting the Applications page used for these modals.
          if (item.bwrphone)     item.bwrphone     = formatHelpers.formatPhoneNumber(item.bwrphone);
          if (item.contactphone) item.contactphone = formatHelpers.formatPhoneNumber(item.contactphone);
          if (item.submittedat)  item.submittedat  = formatHelpers.formatDateTime(item.submittedat);
          this.detailItem = item;
          this.detailOpen = true;
        } catch (err) {
          this.error = err?.response?.data?.message || 'Could not load the application.';
        } finally {
          this.detailLoading = false;
        }
      },
      onKeydown(e) {
        if (e.key === 'Escape' && !this.detailOpen) this.$emit('close');
      },
      scrollThreadToEnd() {
        this.$nextTick(() => {
          const el = this.$refs.thread;
          if (el) el.scrollTop = el.scrollHeight;
        });
      },
      toItem(raw) {
        const item = lowerKeys(raw);
        item.createdat = utcDate(item.createdat);
        return item;
      },
      isMine(item) {
        return !!this.myAzureId && (item.authorazureid || '').toLowerCase() === this.myAzureId;
      },
      stageLabel(code) {
        return this.stages.find(s => s.code === code)?.label || code || 'New Referral';
      },
      when(date) {
        return formatWhen(date);
      },
    },
  };
</script>

<style scoped>
  .rd-overlay { position: fixed; inset: 0; z-index: 1200; background: rgba(10, 20, 40, .25); display: flex; justify-content: flex-end; }
  .rd-drawer {
    width: min(560px, 100vw); height: 100%; background: #f7f8fa; box-shadow: -12px 0 32px rgba(16, 24, 40, .16);
    display: flex; flex-direction: column; overflow: hidden; animation: rd-in .18s ease-out;
  }
  @keyframes rd-in { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }

  .rd-header { display: flex; justify-content: space-between; gap: 12px; padding: 18px 20px; background: #fff; border-bottom: 1px solid #e6e8ec; }
  .rd-header__main { min-width: 0; }
  .rd-header__actions { display: flex; align-items: flex-start; gap: 8px; }
  .rd-type { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #398b87; }
  .rd-title { margin: 2px 0 6px; font-size: 18px; font-weight: 700; color: #111827; overflow-wrap: anywhere; }
  .rd-sub { margin: 0; display: flex; flex-wrap: wrap; gap: 12px; font-size: 12px; color: #6b7280; }
  .rd-sub i { margin-right: 4px; }
  .rd-close { width: 32px; height: 32px; border-radius: 999px; border: 1px solid #d9dce1; background: #fff; cursor: pointer; color: #4b5260; }

  .rd-section { padding: 16px 20px; border-bottom: 1px solid #e6e8ec; background: #fff; }
  .rd-section--thread { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; background: #f7f8fa; border-bottom: 0; }
  .rd-section__head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
  .rd-section__head h3 { margin: 0; font-size: 13px; font-weight: 700; color: #111827; }
  .rd-muted { color: #6b7280; font-size: 12px; }

  /* Stepper: wraps on narrow screens rather than scrolling sideways. */
  .rd-stepper { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px 4px; }
  .rd-stepper.is-dimmed { opacity: .45; }
  .rd-step { display: flex; align-items: center; gap: 6px; padding: 4px 8px 4px 4px; border-radius: 999px; font-size: 12px; color: #6b7280; }
  .rd-step.is-clickable { cursor: pointer; }
  .rd-step.is-clickable:hover { background: #f1f5f9; }
  .rd-step__dot {
    width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; border: 2px solid #d9dce1; background: #fff; color: #9ca3af; flex: 0 0 auto;
  }
  .rd-step.is-done .rd-step__dot { background: var(--step-color); border-color: var(--step-color); color: #fff; }
  .rd-step.is-done { color: #374151; }
  .rd-step.is-current .rd-step__dot { border-color: var(--step-color); color: var(--step-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--step-color) 20%, transparent); }
  .rd-step.is-current { color: #111827; font-weight: 700; }

  .rd-offramp { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 10px; padding: 4px 10px; border: 1px solid; border-radius: 999px; font-size: 12px; font-weight: 700; }
  .rd-offramp-actions { display: flex; gap: 14px; margin-top: 10px; }

  .rd-confirm { margin-top: 12px; padding: 12px; border: 1px solid #e6e8ec; border-radius: 10px; background: #fafbfc; display: flex; flex-direction: column; gap: 8px; }
  .rd-confirm__title { font-size: 13px; color: #111827; }
  .rd-confirm__actions { display: flex; justify-content: flex-end; gap: 8px; }

  .rd-thread { flex: 1 1 auto; min-height: 120px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; padding-right: 4px; }
  .rd-note { align-self: flex-start; max-width: 85%; background: #fff; border: 1px solid #e6e8ec; border-radius: 12px 12px 12px 4px; padding: 8px 12px; }
  .rd-note.is-mine { align-self: flex-end; background: #eef5f4; border-color: #cfe3e1; border-radius: 12px 12px 4px 12px; }
  .rd-note__meta { font-size: 11px; color: #6b7280; margin-bottom: 2px; }
  .rd-note__body { font-size: 13px; color: #111827; white-space: pre-wrap; overflow-wrap: anywhere; }
  .rd-event { display: flex; gap: 8px; font-size: 12px; color: #4b5260; padding: 2px 4px; }
  .rd-event i { color: #9ca3af; margin-top: 2px; }
  .rd-event__body { margin-top: 2px; color: #111827; white-space: pre-wrap; }

  .rd-composer { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; align-items: flex-end; }
  .rd-textarea { width: 100%; box-sizing: border-box; border: 1px solid #e6e8ec; border-radius: 10px; padding: 8px 10px; font: inherit; font-size: 13px; resize: vertical; background: #fff; }
  .rd-textarea:focus { outline: 0; border-color: #398b87; box-shadow: 0 0 0 3px rgba(57, 139, 135, .15); }

  .rd-btn {
    height: 32px; padding: 0 12px; border-radius: 999px; border: 1px solid #d9dce1; background: #fff; color: #111827;
    font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  }
  .rd-btn:hover:not(:disabled) { background: #f6f7f9; }
  .rd-btn:disabled { opacity: .6; cursor: default; }
  .rd-btn--primary { background: var(--primary, #051d3a); border-color: var(--primary, #051d3a); color: #fff; }
  .rd-btn--primary:hover:not(:disabled) { background: #0b2b55; }
  .rd-link { border: 0; background: none; padding: 0; color: #398b87; font-size: 12px; font-weight: 600; cursor: pointer; }
  .rd-link:disabled { color: #9ca3af; cursor: default; }
  .rd-error { margin-bottom: 8px; padding: 8px 10px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 12px; }
</style>
